import { useEffect, Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchSportCounts, setSelectedLeague } from 'stores/match/index.js';
import { fetchOutrightsAllEventsReq } from 'stores/outrights/services';
import { TopLeagues } from 'toolkit/constants.js';
import {
  calculateTimeParameter,
  getSportTypeToActiveIcon,
  getSportTypeToWhiteIcon,
  sportTypeToSportId
} from 'toolkit/utils';
import { calculateSportCount, calculateMatchCountByLeagues } from 'components/base/constants';
import TitleHistoryBack from 'components/general/TitleHistoryBack.js';
import SportDetailCategory from 'components/SportDetailCategory.js';
import SportListItem from 'components/SportListItem.js';
import TournamentCard from 'views/outrights/components/TournamentCard';
import { useAllowedSports } from 'toolkit/useHooks/useAllowedSports';

export default function SportCategories() {
  const { t } = useTranslation('translations');
  const dispatch = useDispatch();
  const { sportType } = useParams();
  const { isSportNameAllowed } = useAllowedSports();
  const [searchParams] = useSearchParams();
  const time = searchParams.get('time');
  const showSportMenu = searchParams.get('show');
  const [selectedSportType, setSelectedSportType] = useState(sportType ? sportType : 'Football');
  const [allOutrightEvents, setAllOutrightEvents] = useState([]);
  const [outrightsSelected, setOutrightsSelected] = useState(false);
  const outrightEventsForSport = allOutrightEvents.filter(
    (e) => e?.se?.sportId === sportTypeToSportId(selectedSportType)
  );
  const hasOutrights = outrightEventsForSport.length > 0;
  const counts = useSelector((state) => {
    if (sportType) {
      return state.match.counts;
    }
    return state.match.timeCounts;
  });
  const categoryNames = useSelector((state) => state.match.categoryNames);

  const getTimeHeading = () => {
    if (time) {
      if (time.includes(' ')) {
        return time.replaceAll(' ', '')[0].toLocaleLowerCase() + time.replaceAll(' ', '').slice(1);
      } else {
        return time[0].toLowerCase() + time.slice(1);
      }
    }
  };

  const getData = async () => {
    try {
      if (sportType) return;
      dispatch(fetchSportCounts({ dateFilter: calculateTimeParameter(time) }));
    } catch (error) {
      // error
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(setSelectedLeague('Today'));
    getData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const events = await fetchOutrightsAllEventsReq();
        if (!cancelled) {
          setAllOutrightEvents(
            (events || []).filter((e) => Array.isArray(e?.markets) && e.markets.length > 0)
          );
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setOutrightsSelected(false);
  }, [selectedSportType]);

  function getLeagues(leagues, leagueName) {
    if (leagues.length > 0) {
      return (
        <Fragment key={leagueName}>
          <h6>{leagueName}</h6>
          <div>{leagues}</div>
        </Fragment>
      );
    } else {
      return null;
    }
  }

  // sport buttons
  function calculateMenuItemsDynamic() {
    const menuItems = [];
    let icon = '';
    if (counts && counts[selectedSportType]) {
      Object.keys(counts).forEach((sportType) => {
        if (!isSportNameAllowed(sportType)) return;
        if (sportType === selectedSportType) {
          icon = getSportTypeToActiveIcon(selectedSportType);
        } else {
          icon = getSportTypeToActiveIcon(sportType);
        }
        const matchCount = calculateSportCount(counts, sportType);
        if (matchCount > 0) {
          menuItems.push(
            <SportDetailCategory
              key={sportType}
              active={selectedSportType === sportType}
              count={matchCount}
              icon={icon}
              onClick={() => {
                setSelectedSportType(sportType);
              }}
              text={t(`sportType.${sportType}`)}
              className={'d-flex flex-column p-2 fs-8'}
            />
          );
        }
      });
      return menuItems;
    }
  }

  function leagueNames() {
    const topLeagues = [];
    const azLeagues = [];
    const leagueArray = [];

    if (counts[selectedSportType]) {
      Object.keys(counts[selectedSportType])
        .sort((a, b) => categoryNames[a]?.localeCompare(categoryNames[b]))
        .forEach((category) => {
          const sportListItem = (
            <SportListItem
              key={category}
              to={`/sport-details/${selectedSportType}/matches?category=${category}${time ? `&time=${time}` : ''
                }`}
              count={calculateMatchCountByLeagues(counts[selectedSportType], category)}
              itemName={category}
              text={t(`categories.${category}`)}
              topLeagueIndex={TopLeagues.indexOf(category)}
            />
          );
          if (TopLeagues.includes(category)) {
            topLeagues.push(sportListItem);
          } else {
            azLeagues.push(sportListItem);
          }
        });
    }
    leagueArray.push(
      getLeagues(
        topLeagues.sort((t1, t2) => t1.props.topLeagueIndex - t2.props.topLeagueIndex),
        t('topLeagues')
      ),
      getLeagues(azLeagues, t('azLeagues'))
    );
    return leagueArray;
  }
  function getAllTab() {
    if (
      (time !== 'Next 4 Hours' &&
        time !== 'Today' &&
        time !== 'Tomorrow' &&
        time !== 'Upcoming Live Matches') ||
      !counts[selectedSportType]
    )
      return null;
    return (
      <SportListItem
        to={`/sport-details/${selectedSportType}/matches?category=all${time ? `&time=${time}` : ''
          }`}
        count={Object.values(counts[selectedSportType])
          .map((c) => Object.values(c))
          .flatMap((c) => c)
          .reduce((partialSum, a) => partialSum + a, 0)}
        text={t(`allcategories`)}
      />
    );
  }

  return (
    <div className="container">
      {showSportMenu && (
        // menu
        <div className="overflow-auto text-nowrap mb-2 bg-dark mx-n3 d-flex py-1 px-2">
          {calculateMenuItemsDynamic()}
        </div>
      )}
      <TitleHistoryBack
        title={t('sport')}
        subTitle={
          sportType
            ? t(`sportType.${sportType}`)
            : t(`pages.desktop.filterSidebar.${getTimeHeading()}`)
        }
      />
      {hasOutrights && (
        <>
          <div className="row">
            <div className="col-1"></div>
            <div className="col-11">
              <button
                type="button"
                className="btn btn-link p-0 w-100 text-decoration-none"
                onClick={() => setOutrightsSelected((v) => !v)}>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fs-6 fw-bold text-start text-primary">
                    {t('outrights.label')}
                  </span>
                  <span className="text-primary">
                    {outrightEventsForSport.length}
                  </span>
                </div>
              </button>
            </div>
          </div>
          <hr className="my-2" />
        </>
      )}
      {outrightsSelected ? (
        <div>
          {outrightEventsForSport.map((event) => (
            <TournamentCard key={event.se?.id} event={event} />
          ))}
        </div>
      ) : (
        <>
          {getAllTab()}
          <div>{leagueNames()}</div>
        </>
      )}
    </div>
  );
}
